import { forwardRef, type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'

/**
 * The box at the head of a to do. Not a Checkbox: a checkbox is a choice beside a label that a Save
 * submits, and its tick is what says checked. A to do's box is a status first, where the thing
 * stands, and a target second: ticking it finishes the thing, at once.
 *
 * Still a native checkbox, so space, `:checked` and a label click come from the platform; only the
 * drawing is its own, in `todo-mark.css`.
 *
 * It sits in a label 6px bigger all round that takes up no room of its own, so a press near the
 * 16px box ticks it too. Hovering or pressing a label hovers and presses its control, so the
 * drawing answers the whole target with no rules of its own.
 */
interface TodoMarkProps extends Omit<ComponentProps<'input'>, 'type' | 'ref'> {
  /**
   * Highlight's share of the work. `drafted` means it has done its half, and the block's left 60%
   * shows in grey. `ready` means its part is finished and only reading is left, so the same 60%
   * shows in blue.
   */
  progress?: 'drafted' | 'ready'
  /** The next move is someone else's: the edge dotted. Stacks on either open or drafted. */
  waiting?: boolean
  /** Due later today, in orange, or overdue, in crimson: the edge and any drafted half. Done drops it. */
  due?: 'today' | 'overdue'
}

/* Forwards its ref, because a to do's mark usually carries a tooltip saying where it stands. */
const TodoMark = forwardRef<HTMLInputElement, TodoMarkProps>(function TodoMark(
  { progress, waiting, due, className, ...props },
  ref,
) {
  return (
    <label
      data-slot="todo-mark-target"
      className="-m-1.5 inline-flex shrink-0 cursor-pointer p-1.5 has-[:disabled]:cursor-default"
      // A press on the padding is the mark's: the click it sends the input is the one that bubbles,
      // where the caller's own `onClick` on the mark sees it.
      onClick={(event) => {
        if (event.target === event.currentTarget) event.stopPropagation()
      }}
    >
      <input
        ref={ref}
        type="checkbox"
        data-slot="todo-mark"
        data-progress={progress}
        data-waiting={waiting ? '' : undefined}
        data-due={due}
        className={twMerge(className)}
        {...props}
      />
    </label>
  )
})

export { TodoMark, type TodoMarkProps }
